import React from "react";
import { ArrowRight, Phone, Star, Leaf, BadgeEuro } from "lucide-react";
import { useTranslation } from "react-i18next";

const HERO_BG = "/brand/vladriver-hero-wide.jpg";
const HERO_BG_MOBILE = "/brand/hero-mobile-alt.jpg";
const GOOGLE_REVIEW_URL = "https://g.page/r/CWM_ouLn_9JqEBM";

const GoogleG = ({ size = 18 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" width={size} height={size} aria-hidden="true">
    <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z" />
    <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z" />
    <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z" />
    <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z" />
  </svg>
);

const Hero = () => {
  const { t } = useTranslation();

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-[88vh] lg:min-h-screen flex items-center pt-28 pb-16 lg:pb-24 bg-[#0A0A0A]"
    >
      {/* Background */}
      <div className="absolute inset-0 overflow-hidden">
        <picture>
          <source media="(max-width: 767px)" srcSet={HERO_BG_MOBILE} />
          <img
            src={HERO_BG}
            alt="VlaDriver VTC Riviera — chauffeur privé éco-responsable sur la Côte d'Azur"
            className="w-full h-full object-cover hero-bg-desktop"
            loading="eager"
            fetchPriority="high"
          />
        </picture>
        {/* Scrims for legibility (content on the right) */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/55 to-[#0A0A0A]/25 lg:via-[#0A0A0A]/40 lg:to-transparent" />
        <div className="absolute inset-0 hidden lg:block bg-gradient-to-l from-[#0A0A0A] via-[#0A0A0A]/80 to-transparent" />
      </div>
      <style>{`
        @media (min-width: 1024px) { .hero-bg-desktop { object-position: left center; } }
      `}</style>

      <div className="container mx-auto px-4 md:px-6 lg:px-12 relative z-10 grid lg:grid-cols-12 gap-8 items-center">
        <div className="hidden lg:block lg:col-span-5" />

        <div className="lg:col-span-7 fade-up">
          <div className="inline-flex items-center gap-2 text-[#D4AF37] text-[10px] md:text-[11px] tracking-[0.3em] uppercase font-medium mb-5">
            <Leaf size={13} /> {t("home.heroOverline")}
          </div>

          <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl xl:text-6xl leading-[1.08] text-white mb-5 max-w-3xl">
            {t("home.heroTitle")}
          </h1>

          <p className="text-white/75 text-base md:text-lg leading-relaxed max-w-2xl mb-6">
            {t("home.heroSubtitle")}
          </p>

          {/* Google Reviews Trust Badge */}
          <a
            href={GOOGLE_REVIEW_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-google-badge"
            className="inline-flex items-center gap-2.5 bg-white/95 hover:bg-white rounded-full pl-3 pr-4 py-2 mb-8 transition-all hover:scale-[1.03] shadow-lg"
          >
            <GoogleG size={18} />
            <span className="flex items-center gap-0.5">
              {[1, 2, 3, 4, 5].map((i) => (
                <Star key={i} size={12} className="fill-[#FBBC04] text-[#FBBC04]" />
              ))}
            </span>
            <span className="text-[#1F1F1F] text-[12px] md:text-[13px] font-semibold">{t("home.heroBadge")}</span>
          </a>

          <div className="flex flex-wrap items-center gap-3">
            <a
              href="#booking"
              data-testid="hero-cta-book"
              className="group inline-flex items-center gap-2 px-6 py-3.5 bg-[#D4AF37] text-[#0A0A0A] text-xs md:text-sm font-semibold uppercase tracking-[0.15em] rounded-full hover:bg-white transition-colors"
            >
              {t("home.heroCtaBook")}
              <ArrowRight size={15} strokeWidth={2} className="group-hover:translate-x-0.5 transition-transform" />
            </a>
            <a
              href="#booking"
              data-testid="hero-cta-price"
              className="inline-flex items-center gap-2 px-6 py-3.5 text-white/90 hover:text-[#D4AF37] text-xs md:text-sm font-semibold uppercase tracking-[0.15em] bg-white/5 hover:bg-white/10 backdrop-blur-md rounded-full transition-colors"
            >
              <BadgeEuro size={15} /> {t("home.heroCtaPrice")}
            </a>
            <a
              href="tel:+33783977370"
              data-testid="hero-cta-call"
              className="inline-flex items-center gap-2 text-white/80 hover:text-[#D4AF37] text-sm transition-colors"
            >
              <Phone size={15} strokeWidth={1.7} />
              <span className="font-mono-price tracking-wide">+33 7 83 97 73 70</span>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
